import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  IconButton,
  Card,
  CardContent,
  Chip,
  Avatar,
  LinearProgress,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import Nav from "../common/Nav.jsx";
import FAQSection from "../common/FAQSection.jsx";
import { getStatusDisplay } from '../../utils/statusUtils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const TicketPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [ticketId, setTicketId] = useState(null);

  // Get ticket data from navigation state or use defaults
  const ticketData = location.state?.ticketData || {
    ticketId: "16222963",
    statusMsg: "Ticket submitted",
    commTypeId: 1,
    refType: 1
  };

  // Get email from navigation state or use default
  const userEmail = location.state?.email || "your email";

  const transaction = location.state?.transaction;

  useEffect(() => {
    setTicketId(ticketData.ticketId);
  }, [ticketData.ticketId]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleGoBack = () => {
    // After ticket creation, back should go to landing (transaction history),
    // not re-open the ticket form the user just submitted.
    navigate('/transaction-history');
  };

  const dateStr = transaction?.date || "2024-11-18T10:10:00";
  const dateObj = new Date(dateStr);

  // Add a helper function for formatting
  function formatDateTime(dateObj) {
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const hh = String(dateObj.getHours()).padStart(2, '0');
    const min = String(dateObj.getMinutes()).padStart(2, '0');
    const ss = String(dateObj.getSeconds()).padStart(2, '0');
    return `${yyyy}/${mm}/${dd} ${hh}:${min}:${ss}`;
  }

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #E9F7FF, #DDEFFF, #FFFFFF)",
        minHeight: "100vh",
      }}
    >
      <Nav />

      <Box
        sx={{
          px: { xs: "16px", sm: "40px", md: "70px" },
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Grid
          container
          spacing={5.4}
          sx={{
            mt: "55px",
            mb: "24px",
            mx: "auto",
            maxWidth: "1280px",
            width: "100%",
          }}
        >
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: { xs: "none", sm: "block" },
              "@media (max-width:734px)": { display: "none" },
            }}
          >
            <Typography
              sx={{
                mb: 2,
                fontWeight: 700,
                fontSize: "20px",
                lineHeight: "24px",
                color: "#253858",
              }}
            >
              Payment Status
            </Typography>
            <Card
              sx={{
                width: "100%",
                height: "auto",
                boxShadow: "none",
                borderRadius: "8px",
                position: "relative",
              }}
            >
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Avatar
                      src={transaction?.logo || "/path-to-blinkit-logo.png"}
                      sx={{ width: 48, height: 48, mr: 2 }}
                    />
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: 400,
                        color: "#253858",
                      }}
                    >
                      {transaction?.merchantName || "Merchant"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
                    <Chip
                      label={getStatusDisplay(transaction?.status).label}
                      sx={{
                        height: 22,
                        borderRadius: "100px",
                        backgroundColor: getStatusDisplay(transaction?.status).backgroundColor,
                        color: getStatusDisplay(transaction?.status).color,
                        fontWeight: 700,
                        fontSize: "12px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "18px",
                        color: "#000000",
                      }}
                    >
                      ₹{transaction?.amount !== undefined ? (Number(transaction.amount) / 100).toFixed(2) : "-"}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
              <Box
                sx={{
                  backgroundColor: "#F4F5F7",
                  border: "2px solid #FFFFFF",
                  borderTop: "1px dashed #DFE1E6",
                  px: 2,
                  py: 1,
                }}
              >
                <Typography
                  sx={{ fontWeight: 500, fontSize: "14px", color: "#253858" }}
                >
                  {transaction?.mode} • {transaction?.transactionId}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    color: "#253858",
                    opacity: 0.6,
                  }}
                >
                  {formatDateTime(dateObj)}
                </Typography>
              </Box>
            </Card>

          </Grid>

          <Grid item xs={11} sm={8}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                mb: 2,
              }}
            >
              <IconButton onClick={handleGoBack} sx={{ p: 0, mr: 1 }}>
                <img src="/Icons/Goback.svg" alt="Go back" />
              </IconButton>
            </Box>

            <Box
              sx={{
                backgroundColor: "#FFFFFF",
                borderRadius: "20px",
                p: { xs: "16px", md: "32px" },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                  maxWidth: "727px",
                  mx: "auto",
                }}
              >
                <CheckCircleIcon sx={{ color: '#36B37E', fontSize: 48, mb: 2 }} />
                <Typography
                  sx={{ fontSize: "18px", fontWeight: 700, color: "#253858" }}
                >
                  We have received your request!
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#253858",
                    mt: 1,
                    px: { xs: 2, md: 0 },
                  }}
                >
                  Your ticket number is <strong>#{ticketData.ticketId}</strong>. A
                  confirmation mail has been sent to{" "}
                  <strong>{userEmail}</strong>.
                </Typography>

                {/* Current Stage Alert */}
                {/* Removed current stage alert */}

                <hr
                  style={{
                    marginTop: "24px",
                    marginBottom: "24px",
                    border: "1px solid #2424241A",
                    width: "100%",
                  }}
                />

                <Paper
                  sx={{
                    borderRadius: "8px",
                    backgroundColor: "#F4F5F7",
                    p: { xs: 2, md: 2 },
                    maxWidth: "525px",
                    width: "100%",
                    mb: 3,
                    boxShadow: "none",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      letterSpacing: "0.016em",
                      lineHeight: "24px",
                      color: "#253858",
                    }}
                  >
                    Our support team will send you an update{" "}
                    <strong>within 10 business days</strong>
                  </Typography>
                </Paper>

                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#36B37E",
                    color: "#fff",
                    textTransform: "none",
                    fontWeight: 500,
                    fontSize: "16px",
                    px: 4,
                    width: { md: "328px", xs: "100%" },
                    borderRadius: "8px",
                    "&:hover": {
                      backgroundColor: "#2da16d",
                    },
                  }}
                  onClick={() => navigate("/")}
                >
                  Done
                </Button>

                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#25385899",
                    mt: 2,
                  }}
                >
                  Awaiting a response to an earlier query?{" "}
                  <Typography
                    component="span"
                    onClick={() => navigate('/check-query', { state: { bypassAuth: true } })}
                    sx={{
                      color: "#0065FF",
                      fontWeight: 500,
                      textDecoration: "none",
                      fontSize: "14px",
                      cursor: "pointer",
                    }}
                  >
                    Track Existing Query
                  </Typography>
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <FAQSection />
    </Box>
  );
};

export default TicketPage;
